import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  Stack,
  TextField,
  MenuItem,
  Button,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link as MuiLink,
  Chip,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { findingsAPI } from '../../services/api';
import { formatTaxonomy, formatNumericWithKind, rankLabel } from './sampleFindings';

/**
 * Cross-corpus taxon search (#51).
 *
 * Asks "where has this taxon shown up?" across the whole corpus. Wraps
 * GET /api/findings/taxon-search (the findings read layer, #50). Results are
 * grouped by project, each row linking through to the specimen analysis tab
 * and the run that produced the finding.
 *
 * Known limitation (surfaced in-UI, deliberately): matching is EXACT string
 * identity of the taxon name. Pipelines using different reference taxonomies
 * (GTDB vs SILVA vs NCBI) can record the same organism under different strings,
 * so results are "under this exact name," not "any name for this organism."
 * The real fix is term harmonization (#53).
 */

const ANY_RANK = '__any__';

function groupByProject(results) {
  const groups = new Map();
  for (const row of results) {
    const key = row.project_id || 'unknown';
    if (!groups.has(key)) {
      groups.set(key, {
        projectId: row.project_id,
        projectNumber: row.project_number,
        rows: [],
      });
    }
    groups.get(key).rows.push(row);
  }
  // Stable order: by project_number ascending, unknowns last.
  return Array.from(groups.values()).sort((a, b) => {
    if (a.projectNumber == null) return 1;
    if (b.projectNumber == null) return -1;
    return a.projectNumber - b.projectNumber;
  });
}

const TaxonSearch = () => {
  const [ranks, setRanks] = useState([]);
  const [name, setName] = useState('');
  const [rank, setRank] = useState(ANY_RANK);

  // Typeahead suggestions (most common in this database first).
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [lastQuery, setLastQuery] = useState(null);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    let active = true;
    findingsAPI
      .getRanks()
      .then((res) => {
        if (active) setRanks(res.data.ranks || []);
      })
      .catch(() => {
        // Rank picker degrades gracefully to "Any rank" only.
        if (active) setRanks([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // Debounced typeahead: fetch suggestions as the user types (>= 2 chars),
  // scoped to the selected rank. Ordered server-side by prevalence.
  useEffect(() => {
    const term = name.trim();
    if (term.length < 2) {
      setOptions([]);
      return undefined;
    }
    let active = true;
    setOptionsLoading(true);
    const timer = setTimeout(() => {
      const params = { q: term };
      if (rank !== ANY_RANK) params.rank = rank;
      findingsAPI
        .taxonSuggest(params)
        .then((res) => active && setOptions(res.data.suggestions || []))
        .catch(() => active && setOptions([]))
        .finally(() => active && setOptionsLoading(false));
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [name, rank]);

  const groups = useMemo(() => groupByProject(results), [results]);

  const runSearch = async (e, override = {}) => {
    if (e) e.preventDefault();
    const trimmed = (override.name ?? name).trim();
    if (!trimmed) return;
    const effectiveRank = override.rank ?? rank;

    setLoading(true);
    setError(null);
    try {
      const params = { name: trimmed };
      if (effectiveRank !== ANY_RANK) params.rank = effectiveRank;
      const res = await findingsAPI.taxonSearch(params);
      setResults(res.data.results || []);
      setTotal(res.data.total || 0);
      setLastQuery({ name: trimmed, rank: effectiveRank !== ANY_RANK ? effectiveRank : null });
      setSearched(true);
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Search failed');
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Deep-link prefill: a ?name=&rank= link (e.g. from the cohort Compare tab)
  // lands here pre-searched, so "drill to specimens" is one click. Runs once.
  useEffect(() => {
    const qName = searchParams.get('name');
    if (!qName) return;
    const qRank = searchParams.get('rank');
    setName(qName);
    if (qRank) setRank(qRank);
    runSearch(null, { name: qName, rank: qRank || ANY_RANK });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Taxon search
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Find every specimen and project where a taxon appears as an analysis finding.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Matches an exact name</AlertTitle>
        Results show findings recorded under this exact taxon name. Pipelines built on
        different reference taxonomies (e.g. GTDB, SILVA, NCBI) may record the same
        organism under a different name, so a single spelling can miss some findings.
      </Alert>

      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box component="form" onSubmit={runSearch}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <Autocomplete
              freeSolo
              fullWidth
              options={options}
              filterOptions={(x) => x}
              loading={optionsLoading}
              inputValue={name}
              onInputChange={(e, val) => setName(val)}
              getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
              renderOption={(props, opt) => (
                <li {...props} key={`${opt.name}-${opt.rank}`}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%' }}
                  >
                    <span>{opt.name}</span>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label={rankLabel(opt.rank)} />
                      <Typography variant="caption" color="text.secondary">
                        {opt.specimen_count} specimen{opt.specimen_count === 1 ? '' : 's'}
                      </Typography>
                    </Stack>
                  </Stack>
                </li>
              )}
              onChange={(e, val) => {
                if (!val) return;
                if (typeof val === 'object') {
                  setName(val.name);
                  runSearch(null, { name: val.name });
                } else {
                  setName(val);
                  runSearch(null, { name: val });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Taxon name"
                  placeholder="Start typing, e.g. Prevotella"
                  autoFocus
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {optionsLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              select
              label="Rank"
              value={rank}
              onChange={(e) => setRank(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={ANY_RANK}>Any rank</MenuItem>
              {ranks.map((r) => (
                <MenuItem key={r} value={r}>
                  {rankLabel(r)}
                </MenuItem>
              ))}
            </TextField>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SearchIcon />}
              disabled={loading || !name.trim()}
              sx={{ mt: { xs: 0, sm: 1 }, whiteSpace: 'nowrap' }}
            >
              Search
            </Button>
          </Stack>
        </Box>
      </Paper>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Searching…</Typography>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && searched && results.length === 0 && (
        <Alert severity="info">
          No findings match{' '}
          <strong>{lastQuery?.name}</strong>
          {lastQuery?.rank ? ` at ${rankLabel(lastQuery.rank)}` : ''}.
        </Alert>
      )}

      {!loading && !error && results.length > 0 && (
        <Stack spacing={3}>
          <Typography variant="subtitle1">
            {total} finding{total === 1 ? '' : 's'} across {groups.length} project
            {groups.length === 1 ? '' : 's'} for <strong>{lastQuery?.name}</strong>
            {lastQuery?.rank ? ` at ${rankLabel(lastQuery.rank)}` : ''}
          </Typography>

          {groups.map((group) => (
            <Paper key={group.projectId || 'unknown'} elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {group.projectId ? (
                  <MuiLink component={RouterLink} to={`/projects/${group.projectId}`}>
                    Project {group.projectNumber ?? '—'}
                  </MuiLink>
                ) : (
                  'Unknown project'
                )}{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  ({group.rows.length} finding{group.rows.length === 1 ? '' : 's'})
                </Typography>
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Specimen</TableCell>
                      <TableCell>Matched rank</TableCell>
                      <TableCell>Lineage</TableCell>
                      <TableCell>Abundance</TableCell>
                      <TableCell>Pipeline</TableCell>
                      <TableCell>Run</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.rows.map((row) => (
                      <TableRow key={row.finding_id} hover>
                        <TableCell>
                          <MuiLink
                            component={RouterLink}
                            to={`/specimens/${row.specimen_id}?tab=analysis`}
                          >
                            {row.specimen_number ?? row.specimen_id}
                          </MuiLink>
                        </TableCell>
                        <TableCell>
                          {row.matched_rank ? (
                            <Chip size="small" label={rankLabel(row.matched_rank)} />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 360 }}>
                          {formatTaxonomy(row.taxonomy)}
                        </TableCell>
                        <TableCell>{formatNumericWithKind(row.abundance)}</TableCell>
                        <TableCell>
                          {row.pipeline_display_name || row.pipeline_name || '—'}
                          {row.pipeline_version ? (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' '}
                              v{row.pipeline_version}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {row.run_number != null ? (
                            <MuiLink
                              component={RouterLink}
                              to={`/analysis/runs/${row.run_number}`}
                            >
                              #{row.run_number}
                            </MuiLink>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default TaxonSearch;
